/**
 * Activity Tracking – personal time tracking (recurring activities + logged sessions).
 * Doc: docs/overall-plan/02-MANGO-PERSONAL/activity-tracking.
 * Wired to /api/v1/personal-activities (personalActivitiesApi) — this used to call the
 * staff-only employee-portal API by mistake, which meant non-staff personal users saw
 * nothing but a "not staff" fallback. This is the real personal-scoped feature.
 */
import React, { useEffect, useState } from 'react';
import { personalActivitiesApi } from '../services/api';

interface PersonalActivity {
  id: string;
  name: string;
  frequency: string;
  durationMinutes?: number | null;
  color?: string;
  isActive: boolean;
}

interface PersonalActivitySession {
  id: string;
  activityId: string;
  startTime: string;
  endTime?: string | null;
  notes?: string | null;
}

const ActivityTracking: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activities, setActivities] = useState<PersonalActivity[]>([]);
  const [sessions, setSessions] = useState<PersonalActivitySession[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newFrequency, setNewFrequency] = useState('daily');
  const [newDuration, setNewDuration] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [runningId, setRunningId] = useState<string | null>(null);

  const loadData = React.useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const [activitiesRes, sessionsRes] = await Promise.all([
        personalActivitiesApi.list(),
        personalActivitiesApi.listSessions(),
      ]);
      if (activitiesRes?.success) setActivities(Array.isArray(activitiesRes.data) ? activitiesRes.data : []);
      else setError(activitiesRes?.error || 'Failed to load activities');
      if (sessionsRes?.success) setSessions(Array.isArray(sessionsRes.data) ? sessionsRes.data : []);
    } catch {
      setError('Failed to load activities');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await personalActivitiesApi.create({
        name: newName.trim(),
        frequency: newFrequency,
        durationMinutes: newDuration ? parseInt(newDuration, 10) : 0,
      });
      if (res?.success) {
        setNewName('');
        setNewDuration('');
        setShowAdd(false);
        loadData(true);
      } else setError(res?.error || 'Failed to add activity');
    } catch {
      setError('Failed to add activity');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStart = async (id: string) => {
    setError(null);
    try {
      const res = await personalActivitiesApi.start(id);
      if (res?.success) setRunningId(id);
      else setError(res?.error || 'Failed to start timer');
    } catch {
      setError('Failed to start timer');
    }
  };

  const handleStop = async (id: string) => {
    setError(null);
    try {
      const res = await personalActivitiesApi.stop(id);
      if (res?.success) {
        setRunningId(null);
        loadData(true);
      } else setError(res?.error || 'Failed to stop timer');
    } catch {
      setError('Failed to stop timer');
    }
  };

  const sessionsForActivity = (activityId: string) => sessions.filter((s) => s.activityId === activityId).slice(0, 3);

  const formatDate = (s: string) => {
    try {
      const d = new Date(s);
      return d.toLocaleDateString(undefined, { dateStyle: 'short' }) + ' ' + d.toLocaleTimeString(undefined, { timeStyle: 'short' });
    } catch {
      return s;
    }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '700px', margin: '0 auto', padding: '40px', textAlign: 'center', color: '#666' }}>
        Loading…
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto', padding: '24px' }}>
      <div
        style={{
          background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          color: 'white',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '26px' }}>📊 Activity Tracking</h1>
            <p style={{ margin: 0, opacity: 0.95, fontSize: '15px' }}>Track time on the things you do regularly</p>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button type="button" onClick={() => setShowAdd((s) => !s)} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.25)', border: '1px solid rgba(255,255,255,0.5)', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>
              Add Activity
            </button>
            <button type="button" onClick={() => loadData(true)} disabled={refreshing} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '8px', color: 'white', cursor: refreshing ? 'not-allowed' : 'pointer', fontSize: '14px' }}>
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {showAdd && (
        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #e2e8f0' }}>
          <input
            type="text"
            placeholder="Activity name (e.g. Reading, Gym, Meditation)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
          />
          <select
            value={newFrequency}
            onChange={(e) => setNewFrequency(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
          <input
            type="number"
            min={0}
            placeholder="Target duration (min, optional)"
            value={newDuration}
            onChange={(e) => setNewDuration(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
          />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={handleAdd} disabled={submitting || !newName.trim()} style={{ padding: '8px 16px', background: '#16a34a', border: 'none', borderRadius: '8px', color: 'white', cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '14px' }}>
              {submitting ? 'Adding…' : 'Save'}
            </button>
            <button type="button" onClick={() => { setShowAdd(false); setNewName(''); }} style={{ padding: '8px 16px', background: '#64748b', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '14px' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && <p style={{ color: '#b91c1c', marginBottom: '16px' }}>{error}</p>}

      {activities.length === 0 && !showAdd ? (
        <div style={{ background: 'white', borderRadius: '16px', padding: '48px 24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', textAlign: 'center' }}>
          <p style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#555' }}>No activities yet. Add one to start tracking time.</p>
          <button type="button" onClick={() => setShowAdd(true)} style={{ padding: '12px 24px', background: '#2c5364', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '15px', fontWeight: 600 }}>
            Add Activity
          </button>
        </div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {activities.map((a) => (
            <li key={a.id} style={{ background: 'white', borderRadius: '12px', padding: '16px', marginBottom: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <div>
                  <span style={{ fontWeight: 600, color: '#333' }}>
                    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: a.color || '#ff6b35', marginRight: 8 }} />
                    {a.name}
                  </span>
                  <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>
                    {a.frequency}
                    {a.durationMinutes ? ` · target ${a.durationMinutes}min` : ''}
                  </span>
                </div>
                {runningId === a.id ? (
                  <button type="button" onClick={() => handleStop(a.id)} style={{ padding: '6px 14px', background: '#dc2626', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '13px' }}>
                    Stop
                  </button>
                ) : (
                  <button type="button" onClick={() => handleStart(a.id)} style={{ padding: '6px 14px', background: '#16a34a', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '13px' }}>
                    Start
                  </button>
                )}
              </div>
              {sessionsForActivity(a.id).length > 0 && (
                <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 0 0', borderTop: '1px solid #eee', paddingTop: '8px' }}>
                  {sessionsForActivity(a.id).map((s) => (
                    <li key={s.id} style={{ fontSize: '12px', color: '#888', marginBottom: '2px' }}>
                      {formatDate(s.startTime)}{s.endTime ? ` – ${formatDate(s.endTime)}` : ' (running)'}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ActivityTracking;
